import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { Coins, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { TokenCostDialog } from "./token-costs/TokenCostDialog";
import { TokenCostsTable } from "./token-costs/TokenCostsTable";
import { InitialTokenAmount } from "./token-costs/InitialTokenAmount";

type TokenCostFormData = {
  model_name: string;
  token_cost: number;
};

export const TokenCostsManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingCost, setEditingCost] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: tokenCosts, isLoading } = useQuery({
    queryKey: ['tokenCosts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('token_model_costs')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: TokenCostFormData) => {
      const { error } = await supabase
        .from('token_model_costs')
        .insert([data]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokenCosts'] });
      toast({
        title: "Success",
        description: "Token cost created successfully",
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create token cost",
        variant: "destructive",
      });
      console.error("Error creating token cost:", error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: TokenCostFormData }) => {
      const { error } = await supabase
        .from('token_model_costs')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokenCosts'] });
      toast({
        title: "Success",
        description: "Token cost updated successfully",
      });
      setIsDialogOpen(false);
      setEditingCost(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update token cost",
        variant: "destructive",
      });
      console.error("Error updating token cost:", error);
    },
  });

  const handleSubmit = (data: TokenCostFormData) => {
    if (editingCost) {
      updateMutation.mutate({ id: editingCost.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (cost: any) => {
    setEditingCost(cost);
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingCost(null);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Coins className="h-6 w-6" />
            Token Costs Management
          </h2>
          <Button onClick={handleAddNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add New Cost
          </Button>
        </div>

        <Card className="p-6">
          {isLoading ? (
            <div>Loading token costs...</div>
          ) : !tokenCosts?.length ? (
            <div className="text-muted-foreground">No token costs configured</div>
          ) : (
            <TokenCostsTable tokenCosts={tokenCosts} onEdit={handleEdit} />
          )}
        </Card>
      </div>

      <InitialTokenAmount />

      <TokenCostDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleSubmit}
        editingCost={editingCost}
      />
    </div>
  );
};